import { LogicalOperators } from "@validereinc/utilities";
import { isPlainObject } from "es-toolkit/compat";

/**
 * Matches a lookup filter placeholder token, e.g. `"%location%"`, which
 * references another form question's answer to be substituted in by
 * {@link resolveLookupFilterPlaceholders}. Captures the referenced
 * question id.
 */
const LOOKUP_FILTER_PLACEHOLDER_PATTERN = /^%(.+)%$/;

/**
 * Extracts the question id referenced by a lookup filter placeholder token
 * (e.g. `"%location%"` -> `"location"`), or `undefined` if `value` is not a
 * placeholder token.
 */
export const getLookupFilterPlaceholderQuestionId = (
  value: unknown
): string | undefined => {
  if (typeof value !== "string") {
    return undefined;
  }

  return LOOKUP_FILTER_PLACEHOLDER_PATTERN.exec(value)?.[1];
};

/**
 * Recursively walks a lookup field's `filter` definition and replaces any
 * `"%questionId%"` placeholder string with the value returned by
 * `resolvePlaceholder` for that question id, leaving all other values
 * untouched.
 *
 * The filter definition can nest plain objects (e.g. operand maps like
 * `{ "$in": "%location%" }`) as well as arrays (e.g. `{ "$or": [...] }` /
 * `{ "$and": [...] }`), so both must be walked for placeholders to resolve
 * correctly at any depth - a filter is not guaranteed to place its
 * placeholders only at the top level.
 */
export const resolveLookupFilterPlaceholders = (
  filter: unknown,
  resolvePlaceholder: (questionId: string) => unknown
): unknown => {
  const placeholderQuestionId = getLookupFilterPlaceholderQuestionId(filter);

  if (placeholderQuestionId !== undefined) {
    return resolvePlaceholder(placeholderQuestionId);
  }

  if (Array.isArray(filter)) {
    return filter.map((item) =>
      resolveLookupFilterPlaceholders(item, resolvePlaceholder)
    );
  }

  if (isPlainObject(filter)) {
    return Object.entries(filter as Record<string, unknown>).reduce(
      (total, [key, value]) => ({
        ...total,
        [key]: resolveLookupFilterPlaceholders(value, resolvePlaceholder),
      }),
      {}
    );
  }

  return filter;
};

/**
 * Recursively checks whether a resolved lookup filter contains an
 * `undefined` value at any depth, walking through both plain objects and
 * arrays (e.g. `$or`/`$and` filter clauses). An `undefined` value indicates
 * an unresolved `"%questionId%"` placeholder (see
 * {@link resolveLookupFilterPlaceholders}) whose referenced question has not
 * been answered yet.
 *
 * An `$or` array is unresolved only when every branch is unresolved.
 */
const containsUnresolvedPlaceholder = (
  filter: unknown,
  key?: string
): boolean => {
  if (filter === undefined) {
    return true;
  }

  if (Array.isArray(filter)) {
    if (key === LogicalOperators.or) {
      return filter.every((branch) => containsUnresolvedPlaceholder(branch));
    }

    return filter.some((item) => containsUnresolvedPlaceholder(item));
  }

  if (isPlainObject(filter)) {
    return Object.entries(filter as Record<string, unknown>).some(
      ([entryKey, value]) => containsUnresolvedPlaceholder(value, entryKey)
    );
  }

  return false;
};

/**
 * Determines whether a lookup field's filter object is complete enough to
 * enable a data fetch. `undefined` is an unresolved `"%questionId%"`
 * placeholder. No filter means fetch unfiltered.
 */
export const isLookupFilterValid = (
  filter: Record<string, unknown> | undefined
): boolean => {
  if (!filter) {
    return true;
  }

  return !containsUnresolvedPlaceholder(filter);
};
